import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { QuestionBrowseTaxonomy } from '@course-authoring/shared-types';
import { QUESTION_BANK_BROWSE_COPY as COPY } from './questionBankBrowseCopy';
import type { QuestionBankBrowseStyles } from './questionBankBrowseStyles';
import { QuestionBankTaxonomySections } from './QuestionBankTaxonomySections';

/** The popover's distance under the pill, its tallest (prototype), and the room kept under it inside the viewport. */
const POPOVER_GAP = 8;
const POPOVER_MAX_HEIGHT = 430;
const VIEWPORT_MARGIN = 16;

export interface QuestionBankTaxonomyPopoverProps {
  styles: QuestionBankBrowseStyles;
  taxonomies: readonly QuestionBrowseTaxonomy[];
  checkedLabelIds: readonly number[];
  openGroups: ReadonlySet<string>;
  onSetGroups: (keys: readonly string[], open: boolean) => void;
  onToggleLabel: (labelId: number) => void;
}

interface Placement {
  top: number;
  right: number;
  maxHeight: number;
}

/** Whether the dialog holding `node` is the topmost one: no detail or concepts dialog is open over it. */
const inTopmostDialog = (node: Node | null): boolean => {
  const dialogs = document.querySelectorAll('[role="dialog"]');
  const topmost = dialogs[dialogs.length - 1];
  return !!topmost && !!node && topmost.contains(node);
};

/**
 * The picker's "Skills & risk areas" pill (Sara's "Choose a question"
 * prototype, 2026-11-03): the page's framework sections in a popover under
 * the pill, since the dialog has no room for the panel column. The pill
 * shows how many labels are checked ("All" when none). Checking a box
 * leaves the popover open; a click elsewhere or Escape closes it, and that
 * Escape never reaches the dialog. The popover is placed against the
 * viewport from the pill's position, so the dialog's overflow clipping
 * cannot cut it short when the list is shorter than it is. The caller
 * renders the pill only while Purpose is Sentiment.
 */
export function QuestionBankTaxonomyPopover({
  styles,
  taxonomies,
  checkedLabelIds,
  openGroups,
  onSetGroups,
  onToggleLabel,
}: QuestionBankTaxonomyPopoverProps) {
  const b = styles.browse;
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const pillRef = useRef<HTMLButtonElement | null>(null);
  const popoverId = useId();
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<Placement | null>(null);

  // Under the pill, right edges aligned, no taller than the prototype or than the viewport leaves.
  const measure = useCallback(() => {
    const rect = pillRef.current?.getBoundingClientRect();
    if (!rect) return;
    const top = rect.bottom + POPOVER_GAP;
    const next: Placement = {
      top,
      right: Math.max(0, window.innerWidth - rect.right),
      maxHeight: Math.max(0, Math.min(POPOVER_MAX_HEIGHT, window.innerHeight - top - VIEWPORT_MARGIN)),
    };
    setPlacement((current) => (
      current && current.top === next.top && current.right === next.right && current.maxHeight === next.maxHeight ? current : next
    ));
  }, []);
  useLayoutEffect(() => {
    if (!open) {
      setPlacement(null);
      return undefined;
    }
    measure();
    // The dialog is sized to its content and centred, so a shorter or longer list moves the pill; follow it.
    const frame = pillRef.current?.closest('[role="dialog"]')?.firstElementChild ?? null;
    const observer = frame && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (frame) observer?.observe(frame);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [open, measure]);
  // And after every render while open, for a move the observer has not reported yet (the list re-rendering).
  useLayoutEffect(() => {
    if (open) measure();
  });

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (event.target instanceof Node && !wrapRef.current?.contains(event.target)) setOpen(false);
    };
    // Capture phase on the document, so the dialog's own Escape handling (which closes the picker) never sees
    // this press. Only while the picker is the topmost dialog: a detail or concepts dialog opened from the
    // keyboard leaves the popover open underneath, and Escape must close that dialog, not the hidden popover.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !inTopmostDialog(wrapRef.current)) return;
      event.stopPropagation();
      event.preventDefault();
      setOpen(false);
      if (wrapRef.current?.contains(document.activeElement)) pillRef.current?.focus();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  const count = checkedLabelIds.length;
  return (
    <span ref={wrapRef} style={b.taxonomyWrap}>
      <button
        ref={pillRef}
        type="button"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        aria-label={`${COPY.taxonomyPill}: ${count > 0 ? COPY.checkedCount(count) : COPY.taxonomyAll}`}
        style={{ ...b.taxonomyPill, ...(open ? b.taxonomyPillOpen : {}) }}
        onClick={() => setOpen((current) => !current)}
      >
        <span style={{ ...b.fieldName, ...(open ? b.taxonomyPillNameOpen : {}) }}>{`${COPY.taxonomyPill}:`}</span>
        <span style={b.fieldValue}>{count > 0 ? count : COPY.taxonomyAll}</span>
        <span aria-hidden="true" style={{ ...b.taxonomyCaret, ...(open ? b.taxonomyCaretOpen : {}) }} />
      </button>
      {open && placement ? (
        <div
          id={popoverId}
          role="group"
          aria-label={COPY.panelLabel}
          style={{ ...b.taxonomyPopover, top: placement.top, right: placement.right, maxHeight: placement.maxHeight }}
          data-testid="taxonomy-popover"
        >
          <QuestionBankTaxonomySections
            styles={styles}
            taxonomies={taxonomies}
            checkedLabelIds={checkedLabelIds}
            openGroups={openGroups}
            onSetGroups={onSetGroups}
            onToggleLabel={onToggleLabel}
          />
        </div>
      ) : null}
    </span>
  );
}
